import React, { FC, SVGProps, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { Spacing, SpacingDTM, ImgContainFit, ImgWH, LabelColor } from '@pages/CommonStyle/common.style';
import { constSopBanner } from '@assets/images/solution';
import {
  ScreenViewButtonLayout,
  ScreenViewImageSection,
  ScreenViewButtonStyled,
  ProgressCapsule, // styled.svg
  buildCapsulePath,
  ScreenViewIcon,
  ScreenViewButtonWrapper,
  ScreenViewButtonIcon, // (width, height, r?) → { viewBox, d }
} from './ScreenView.style';
import { StyledDisplay } from '@components/Text';
import { DivAllCenter } from '@pages/CommonStyle/commonDiv.style';
import { StyledGridContainer } from '@components/Grid/Grid.style';

interface ScreenItem {
  icon: string | FC<SVGProps<SVGSVGElement>>;
  iconOn?: string;
  text: string;
  img: string;
}
interface ScreenViewProps {
  pageName: string;
  title: string;
  data: ScreenItem[];
}

const ScreenView: React.FC<ScreenViewProps> = ({ pageName, title, data }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const active = activeRef.current;
    if (!wrapper || !active) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();

    // 스크롤해야 할 양 = 현재 버튼의 좌측 위치 - wrapper 기준 왼쪽 + 현재 scrollLeft - 목표 위치(100px)
    const offset = active.offsetLeft - 100;

    wrapper.scrollTo({
      left: offset,
      behavior: 'smooth',
    });
  }, [activeIndex]);

  /* 5초마다 자동 전환 */
  useEffect(() => {
    const t = setTimeout(() => setActiveIndex((i) => (i + 1) % data.length), 4000);
    return () => clearTimeout(t);
  }, [activeIndex, data.length]);

  /* ────────────────────────────  Ring용 동적 Path 계산  */
  const activeRef = useRef<HTMLDivElement>(null);
  const [svgData, setSvgData] = useState<{ viewBox: string; d: string } | null>(null);

  useLayoutEffect(() => {
    const el = activeRef.current;
    if (!el) return;
    /* 버튼 border(2) + 링 padding(2) → 총 4 px 추가 */
    const { width, height } = el.getBoundingClientRect();
    setSvgData(buildCapsulePath(width, height)); // r 기본 28px
  }, [activeIndex]); // activeIndex 바뀔 때마다 재계산
  /* ──────────────────────────── */

  /* 클릭 핸들러 */
  const handleClick = (i: number) => setActiveIndex(i);

  /* ────────────────────────────  렌더  */
  return (
    <StyledGridContainer>
      <DivAllCenter>
        <SpacingDTM d="128" t="128" m="96" />
        <StyledDisplay size="small">
          <Trans i18nKey={`${pageName}.screenView.title`} />
        </StyledDisplay>
        <Spacing pixel="32" />

        <ScreenViewButtonWrapper ref={wrapperRef}>
          <ScreenViewButtonLayout>
            {data.map((btn, i) => {
              const isActive = i === activeIndex;
              const IconComponent = btn.icon;

              return (
                <ScreenViewButtonStyled key={i} ref={isActive ? activeRef : undefined} /* active 버튼만 측정 */ onClick={() => handleClick(i)}>
                  {/* 진행 링 */}
                  {isActive && svgData && (
                    <ProgressCapsule viewBox={svgData.viewBox} preserveAspectRatio="none">
                      {/* pathLength는 **속성**(attribute) → 문자열 or 숫자 OK */}
                      <path d={svgData.d} pathLength={100} />
                    </ProgressCapsule>
                  )}

                  {/* 아이콘 + 라벨 */}
                  {typeof btn.icon === 'string' ? (
                    <ScreenViewButtonIcon src={isActive ? btn.iconOn ?? btn.icon : btn.icon} />
                  ) : (
                    (() => {
                      const IconComponent = btn.icon;
                      return <IconComponent width={20} height={20} style={{ transform: 'scale(0.8)', color: isActive ? '#2A70F0' : '#818181' }} />;
                    })()
                  )}

                  <LabelColor size="small" color={isActive ? '#2A70F0' : '#818181'}>
                    <Trans i18nKey={`${pageName}.screenView.data.${i}.text`} />
                  </LabelColor>
                </ScreenViewButtonStyled>
              );
            })}
          </ScreenViewButtonLayout>
        </ScreenViewButtonWrapper>

        <Spacing pixel="48" />
        <ScreenViewImageSection>
          <ImgContainFit src={data[activeIndex]?.img || constSopBanner} />
        </ScreenViewImageSection>
        <SpacingDTM d="128" t="128" m="96" />
      </DivAllCenter>
    </StyledGridContainer>
  );
};

export default ScreenView;
